/**
 * Admin Integrations Page for Olympic Fit Wellness
 * Allows admins to manage third-party integration credentials
 */
import { useState, useEffect } from 'react';
import { 
  Link2, 
  Check, 
  X, 
  AlertCircle, 
  ExternalLink, 
  Eye, 
  EyeOff,
  Loader2,
  Settings,
  TestTube
} from 'lucide-react';
import api from '../../lib/api';

export default function AdminIntegrationsPage() {
  const [categories, setCategories] = useState([]);
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [configuredIntegrations, setConfiguredIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [credentials, setCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cats, available, configured] = await Promise.all([
        api.getIntegrationCategories(),
        api.getAvailableIntegrations(),
        api.getConfiguredIntegrations()
      ]);
      
      setCategories(cats);
      // available now includes is_configured and current_values from backend
      setAvailableIntegrations(available);
      setConfiguredIntegrations(configured);
    } catch (error) {
      console.error('Error loading integrations:', error);
      setMessage({ type: 'error', text: 'Failed to load integrations' });
    } finally {
      setIsLoading(false);
    }
  };

  const openIntegrationModal = async (integration) => {
    setSelectedIntegration(integration);
    setShowPasswords({});
    
    // Pre-fill with current values from the integration data
    if (integration.current_values) {
      setCredentials(integration.current_values);
    } else {
      setCredentials({});
    }
    
    // Show info message if already configured
    if (integration.is_configured) {
      setMessage({ type: 'info', text: 'This integration is already configured. Enter new credentials to update.' });
    } else {
      setMessage(null);
    }
  };

  const handleSave = async () => {
    if (!selectedIntegration) return;
    
    setIsSaving(true);
    setMessage(null);
    
    try {
      await api.configureIntegration(selectedIntegration.name, credentials);
      setMessage({ type: 'success', text: 'Integration configured successfully!' });
      await loadData();
      setTimeout(() => setSelectedIntegration(null), 1500);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save integration' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async (name) => {
    setIsTesting(true);
    setMessage(null);
    
    try {
      const result = await api.testIntegration(name);
      setMessage({ type: 'success', text: result.message || 'Connection test successful!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Connection test failed' });
    } finally {
      setIsTesting(false);
    }
  };

  const handleToggle = async (name) => {
    try {
      await api.toggleIntegration(name);
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const filteredIntegrations = selectedCategory === 'all' 
    ? availableIntegrations 
    : availableIntegrations.filter(i => i.category === selectedCategory);

  const getConfigStatus = (name) => {
    // First check if the integration has is_configured from availableIntegrations
    const available = availableIntegrations.find(i => i.name === name);
    if (available && available.is_configured) {
      return { is_configured: true, is_active: true };
    }
    // Fallback to configuredIntegrations from database
    return configuredIntegrations.find(c => c.name === name);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-integrations">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Third-Party Integrations</h1>
          <p className="text-gray-600 mt-1">Configure API keys and credentials for external services</p>
        </div>
      </div>

      {/* Message */}
      {message && !selectedIntegration && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' :
          message.type === 'error' ? 'bg-red-50 text-red-700' :
          'bg-blue-50 text-blue-700'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> :
           message.type === 'error' ? <AlertCircle className="w-5 h-5" /> :
           <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedCategory === 'all'
              ? 'bg-dark text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Integrations
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              selectedCategory === cat.id
                ? 'bg-dark text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.name}
          </button>
        ))}
      </div>

      {/* Integrations Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => {
          const status = getConfigStatus(integration.name);
          
          return (
            <div 
              key={integration.name}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{integration.icon}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{integration.display_name}</h3>
                      <span className="text-xs text-gray-500 capitalize">{integration.category}</span>
                    </div>
                  </div>
                  
                  {status && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      status.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : status.is_configured 
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {status.is_active ? 'Active' : status.is_configured ? 'Configured' : 'Inactive'}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
                
                {integration.note && (
                  <p className="text-xs text-cyan bg-cyan/10 p-2 rounded mb-4">{integration.note}</p>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={() => openIntegrationModal(integration)}
                    className="flex-1 btn-primary text-sm py-2"
                    data-testid={`configure-${integration.name}`}
                  >
                    <Settings className="w-4 h-4 inline mr-2" />
                    Configure
                  </button>
                  
                  {status?.is_configured && (
                    <>
                      <button
                        onClick={() => handleTest(integration.name)}
                        className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={isTesting}
                        title="Test Connection"
                      >
                        {isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={() => handleToggle(integration.name)}
                        className={`px-3 py-2 rounded-lg transition-colors ${
                          status.is_active 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={status.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {status.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </>
                  )}
                </div>
                
                {integration.docs_url && (
                  <a
                    href={integration.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center gap-1 text-xs text-gray-500 hover:text-cyan"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Documentation
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Configuration Modal */}
      {selectedIntegration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{selectedIntegration.icon}</span>
                  <div>
                    <h2 className="text-xl font-heading font-bold">{selectedIntegration.display_name}</h2>
                    <p className="text-sm text-gray-500">Configure credentials</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedIntegration(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {message && (
                <div className={`p-3 rounded-lg text-sm ${
                  message.type === 'success' ? 'bg-green-50 text-green-700' :
                  message.type === 'error' ? 'bg-red-50 text-red-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  {message.text}
                </div>
              )}
              
              {selectedIntegration.required_fields?.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  
                  {field.type === 'textarea' ? (
                    <textarea
                      rows={4}
                      value={credentials[field.key] || ''}
                      onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                      className="w-full input-field"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={credentials[field.key] || ''}
                      onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                      className="w-full input-field"
                    >
                      <option value="">Select {field.label}</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="relative">
                      <input
                        type={showPasswords[field.key] ? 'text' : (field.type === 'password' ? 'password' : 'text')}
                        value={credentials[field.key] || ''}
                        onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                        className="w-full input-field pr-10"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                      {field.type === 'password' && (
                        <button
                          type="button"
                          onClick={() => setShowPasswords({...showPasswords, [field.key]: !showPasswords[field.key]})}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {selectedIntegration.docs_url && (
                <a
                  href={selectedIntegration.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-cyan hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View documentation for {selectedIntegration.display_name}
                </a>
              )}
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <button
                onClick={() => setSelectedIntegration(null)}
                className="flex-1 py-3 border border-gray-200 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 btn-primary py-3 flex items-center justify-center gap-2"
                data-testid="save-integration"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
